import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Divider,
  Grid,
  Stack,
  Skeleton,
  TextField,
  Typography,
  Button,
  Autocomplete,
  FormControlLabel,
} from "@mui/material";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import InfoIcon from "@mui/icons-material/Info";
import { debounce } from "lodash";
import moment from "moment";
import * as Yup from "yup";
import { Tooltip, IconButton, Switch } from "@financeable-com-au/financeable-ui";
import { usePaidServices, useDemoCommercialApplication } from "@/hooks";
import { PaidServicesTooltip } from "@/components/PaidServicesTooltip";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import { showProfessionalServices } from "./utils/entityUtils";
import EntityFinancialStatement from "./EntityFinancialStatement";
import EntityFinancialCommitments from "./EntityFinancialCommitments";
import EntityOtherDocuments from "./EntityOtherDocuments";
import { EntityCommercialCreditChecks } from "./EntityCommercialCreditChecks";
import { EntityServiceCardGroup } from "./EntityServiceCard";
import setTicksAndCrosses from "../../utils/setTicksAndCrosses";
import regex from "../../utils/regex";
import { getErrorToastPayload, getSuccessToastPayload } from "../../utils/commonFunctions";
import {
  ERRORS_MESSAGE,
  SUCCESS_MESSAGE,
  entityType,
  entityStatus,
  industryList,
  validEntityStatus,
  FILE_AWAITING_MESSAGE,
  validIndustryList,
  professionalServicesList,
  ENTITY_SERVICE_CHECKS,
  ENTITY_SERVICE_CHECK_COPY,
  SERVICE_PROVIDER_LOGOS,
} from "../../constants";
import { userPermissionsSelector } from "../../store/slices/userSlice";
import {
  createEntity,
  entitySelector,
  updateEntity,
  saveValue,
  getASICData,
  userDetailsSelector,
  removeEntity,
  getCommercialCreditFile,
  setValidationRequireFieldError,
} from "../../store/slices/applicationFormSlice";
import { getBusinessByABN } from "../../services/abr";
import readableABNString from "../../utils/readableABNString";
import readableACNString from "../../utils/readableACNString";
import Document from "../CustomerDetails/Document";
import { formatAmount, transformCreditHistoryName } from "../Utils/helperFunction";
import GoogleAddressSearch from "../Utils/GoogleAddressSearch";
import ConfirmationDialog from "../customComponents/ConfirmationDialog";
import CreditScoreGradientGraph from "../customComponents/CreditScoreGradientGraph";
import ManualAddress from "../CustomerDetails/ManualAddress";
import { openSnackbar } from "../../store/slices/snackbarSlice";

const Entity = React.memo(
  ({ application, entitys, index, entities, fieldsErrors, setFieldsErrors, isDisableEdit, hideAdvancedActions = false }) => {
  const { canUsePaidServices } = usePaidServices();
  const { isDemoCommercialApplication } = useDemoCommercialApplication();
  const dispatch = useDispatch();
  const permissions = useSelector(userPermissionsSelector);
  const servicePermission = permissions?.services;
  const { accessComponents } = useAccessAppComponent();
  const {
    application: {
      entity: {
        isAsicExtractEnabled,
        isAsicExtractPPSREnabled,
        isEquifaxCommercialReportEnabled,
        isEquifaxScoredCommercialReportEnabled,
      },
    },
  } = accessComponents;
  const applicationId = application?._id;

  const [scoredEquifaxModalOpen, setScoredEquifaxModalOpen] = useState(false);
  // The ASIC and Equifax loaders only say which entity is loading, so this says which of its cards to spin.
  const [activeServiceCheck, setActiveServiceCheck] = useState(null);

  const {
    gettingASICData,
    gettingCreditProfile,
    gettingEntityDetails,
    gettingEquifaxCreditCompanyFile,
    validationRequireFieldError,
    isManualAddress,
    gettingAddressDetails,
  } = useSelector(userDetailsSelector);
  const { entityFieldErrors } = validationRequireFieldError;

  const entity = useSelector(entitySelector);
  const [businessSearchValue, setBusinessSearchValue] = useState({
    searchValueBusiness: "",
    businessSearchInputText: "",
  });

  const [turnover, setTurnoverValue] = useState({});
  const [continuity, setContinuity] = useState(entitys?.continuity || false);
  const [creditHistoryValue, setCreditHistoryValue] = useState([]);
  const [creditHistoryDefaults, setCreditHistoryDefaults] = useState({
    paymentDefaultsList: [],
    categoryDefaultsList: [],
    statusDefaultsList: [],
  });
  const [industryTypeValue, setIndustryTypeValue] = useState(entitys?.industryType || "");

  // Professional Services.
  const [professionalServices, setProfessionalServices] = useState(entitys?.professionalServices || "");
  const isProfessionalServicesShown = showProfessionalServices(industryTypeValue);
  const sortedIndustryList = [...industryList].sort((a, b) => a.label.localeCompare(b.label));

  const numOfColumns = 3;
  const numOfRows = 2;

  const columnLayouts = [
    [4, 12],
    [4, 12, 12],
  ];

  const validationSchema = Yup.object({
    turnover: Yup.string()
      .optional()
      .matches(regex.allowOnlyNumber, "Only numbers and decimal points allowed.")
      .max(12, "Maximum of 12 characters"),
    industryType: Yup.string().optional().oneOf(validIndustryList, "Please select valid industry type"),
    professionalServices: Yup.string()
      .optional()
      .oneOf([...professionalServicesList, ""], "Please select a valid professional service"),
    entityAddress: Yup.string()
      .required("address field is required")
      .min(3, "Maximum of 3 characters")
      .max(200, "Maximum of 200 characters"),
  });

  const validateField = async (fieldName, value) => {
    try {
      const name = fieldName.split("_")[0];
      await validationSchema.validateAt(name, { [name]: value });
      dispatch(
        setValidationRequireFieldError({
          ...validationRequireFieldError,
          entityFieldErrors: { ...entityFieldErrors, [fieldName]: "" },
        }),
      );
    } catch (error) {
      dispatch(
        setValidationRequireFieldError({
          ...validationRequireFieldError,
          entityFieldErrors: { ...entityFieldErrors, [fieldName]: error.message },
        }),
      );
    }
  };

  const handle = {
    entityFn: (update) => {
      dispatch(updateEntity(update));
    },
    entityDetailsFn: async (details) => {
      dispatch(saveValue({ gettingEntityDetails: true }));
      const abrData = await getBusinessByABN(details.Abn);

      if (abrData.EntityName !== entity?.entityName) {
        dispatch(createEntity({ abrData, applicationId })).then((res) => {
          if (res?.error) {
            dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
          }
        });
      } else {
        dispatch(saveValue({ gettingEntityDetails: false }));
      }
    },
    loadingGridFirst: (
      <Grid item container spacing={2} style={{ marginBottom: "15px" }}>
        {Array.from({ length: numOfColumns * numOfRows }).map((_, index) => (
          <Grid key={index} item sm={4}>
            <Skeleton variant="text" animation="wave" />
          </Grid>
        ))}
      </Grid>
    ),
    loadingGridSecond: (
      <Grid item container spacing={2}>
        {columnLayouts.map((columns, rowIndex) => (
          <Grid key={rowIndex} item container>
            {columns.map((sm, columnIndex) => (
              <Grid key={columnIndex} item sm={sm}>
                <Skeleton variant="text" />
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    ),
    onChangeFieldTurnover: async (e, fieldName) => {
      // const name = fieldName.split("_")[0];
      let isValid = true;

      isValid = regex.allowOnlyNumber.test(e) && e?.length <= 12;

      if (!isValid) {
        await validateField(fieldName, e);
        return;
      } else {
        setTurnoverValue({ [entitys._id]: e });
        await validateField(fieldName, e);
      }
    },
    onChangeFieldIndustryType: async (e, name) => {
      if (!e) {
        setIndustryTypeValue("");
        await validateField(name, e);
        return;
      } else {
        setIndustryTypeValue(e);
        await validateField(name, e);
      }
    },
    onChangeFieldContinuity: async (event) => {
      const checked = event.target.checked;
      setContinuity(checked);
      dispatch(
        updateEntity({
          applicationId,
          entityId: entitys?._id,
          continuity: checked,
        }),
      );
    },
    onChangeFieldProfessionalServices: async (fieldName, value) => {
      try {
        // Validate the field.
        const name = fieldName.split("_")[0];
        await validationSchema.validateAt(name, { [name]: value });
        // If valid, save it straight away so the lender products re-evaluate against it.
        dispatch(
          updateEntity({
            applicationId,
            entityId: entitys?._id,
            professionalServices: value,
          }),
        ).then((res) => {
          if (res?.error) {
            dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
          }
        });
        // Clear the error.
        dispatch(
          setValidationRequireFieldError({
            ...validationRequireFieldError,
            entityFieldErrors: { ...entityFieldErrors, [fieldName]: "" },
          }),
        );
      } catch (error) {
        // If invalid, set the error.
        dispatch(
          setValidationRequireFieldError({
            ...validationRequireFieldError,
            entityFieldErrors: { ...entityFieldErrors, [fieldName]: error.message },
          }),
        );
      }
    },
    addAddress: (newAddressValue) => {
      if (newAddressValue) {
        dispatch(
          updateEntity({
            applicationId,
            entityId: entitys._id,
            address: newAddressValue,
            isManualAddress: false, // This is google address add
          }),
        );
      }
    },
    updateAddress: (newAddressValue) => {
      if (newAddressValue) {
        dispatch(updateEntity({ ...newAddressValue, applicationId }));
      }
    },
    removeAddress: (newAddressValue) => {
      if (newAddressValue) {
        dispatch(
          updateEntity({
            applicationId,
            entityId: entitys._id,
            addressId: newAddressValue?._id,
          }),
        );
      }
    },
    addManualAddress: (entityId) => {
      dispatch(saveValue({ isManualAddress: { show: true, entityId } }));
    },
    getAsicExtract: (check) => {
      setActiveServiceCheck(check);
      dispatch(saveValue({ gettingASICData: entitys?._id }));
      dispatch(
        getASICData({
          acn: entitys?.acn,
          applicationId,
          entityId: entitys?._id,
          ...(check === ENTITY_SERVICE_CHECKS.ASIC_EXTRACT_PPSR && { ppsr: true }),
        }),
      ).then((res) => {
        const updatedEntities = res?.payload?.data?.entities;
        if (!updatedEntities) {
          dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
          return;
        }

        // The extract returns the entity's registered addresses, so the address entered before it is dropped.
        const updatedEntity = updatedEntities.find((item) => item?._id === entitys?._id);
        if (updatedEntity?.addresses?.length && entitys?.addresses?.length > 0) {
          handle.removeAddress(entitys.addresses[0]);
        }
        dispatch(openSnackbar(getSuccessToastPayload(res, SUCCESS_MESSAGE.fetchMsg)));
      });
    },
    getEquifaxReport: (check) => {
      setActiveServiceCheck(check);
      dispatch(saveValue({ gettingCreditProfile: entitys?._id }));
      dispatch(
        getCommercialCreditFile({
          entityId: entitys?._id,
          applicationId,
          ...(check === ENTITY_SERVICE_CHECKS.EQUIFAX_SCORED_REPORT && { isScored: true }),
        }),
      ).then((res) => {
        const reportData = res?.payload?.data;
        dispatch(
          openSnackbar(
            reportData && Object.keys(reportData).length > 0
              ? getSuccessToastPayload(res, SUCCESS_MESSAGE.fetchMsg)
              : getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg),
          ),
        );
      });
    },
  };

  const debounced = debounce(async () => {}, 500);

  useEffect(() => {
    // const withValueArray = [];
    const publicRecordValueArray = [];
    const creditHistoryValueArray = [];
    const creditDefaults = {
      paymentDefaultsList: [],
      categoryDefaultsList: [],
      statusDefaultsList: [],
    };

    if (entitys?.creditProfile?.publicReports) {
      Object.values(entitys?.creditProfile?.publicReports).forEach((category) => {
        if (Array.isArray(category)) {
          category.forEach((item) => {
            const temp = {
              ...item,
              amount: category.find((record) => record.name === `${item.name}_Value`)?.amount,
            };
            if (item.name && !item.name.includes("_Value")) {
              // withValueArray.push(item);
              publicRecordValueArray.push(temp);
            }
            // else {
            //   publicRecordValueArray.push(temp);
            // }
          });
        }
      });
    }

    // Set credit History below
    if (entitys?.creditProfile?.creditHistory) {
      const entityCreditHistory = entitys?.creditProfile?.creditHistory;
      Object.values(entityCreditHistory).forEach((category) => {
        if (Array.isArray(category)) {
          category.forEach((item) => {
            const temp = {
              ...item,
              amount: category.find((record) => record.name === `${item.name}_Value`)?.amount,
            };
            if (item.name && !item.name.includes("_Value")) {
              creditHistoryValueArray.push(temp);
            }
          });
        }
      });

      setCreditHistoryValue(creditHistoryValueArray);

      // Set credit profile payment Defaults
      Object.values(entityCreditHistory?.paymentDefaultsList).forEach((category) => {
        if (Array.isArray(category)) {
          category.forEach((item) => {
            const temp = {
              ...item,
              amount: category.find((record) => record.name === `${item.name}_Value`)?.amount,
            };
            if (item.name && (item.name.includes("_Value") || item.name.includes("-Value"))) {
              // Do nothing
            } else {
              creditDefaults?.paymentDefaultsList.push(temp);
            }
          });
        }
      });

      // Set credit profile category Defaults
      Object.values(entityCreditHistory?.categoryDefaultsList).forEach((category) => {
        if (Array.isArray(category)) {
          category.forEach((item) => {
            const temp = {
              ...item,
              amount: category.find((record) => record.name === `${item.name}_Value`)?.amount,
            };
            if (item.name && (item.name.includes("_Value") || item.name.includes("-Value"))) {
              // Do nothing
            } else {
              creditDefaults?.categoryDefaultsList.push(temp);
            }
          });
        }
      });

      // Set credit profile status Defaults
      Object.values(entityCreditHistory?.statusDefaultsList).forEach((category) => {
        if (Array.isArray(category)) {
          category.forEach((item) => {
            const temp = {
              ...item,
              amount: category.find((record) => record.name === `${item.name}_Value`)?.amount,
            };
            if (item.name && !item.name.includes("-Value")) {
              creditDefaults?.statusDefaultsList.push(temp);
            }
          });
        }
      });

      setCreditHistoryDefaults(creditDefaults);
    }
  }, [entitys?.creditProfile]);

  useEffect(() => {
    if (!gettingASICData) {
      dispatch(saveValue({ gettingASICData: null }));
    }
  }, [gettingASICData]);

  useEffect(() => {
    debounced(businessSearchValue?.businessSearchInputText);
    return () => debounced.cancel();
  }, [businessSearchValue?.searchValueBusiness, businessSearchValue?.businessSearchInputText]);

  useEffect(() => {
    if (!gettingEntityDetails) {
      setBusinessSearchValue({
        searchValueBusiness: "",
        businessSearchInputText: "",
      });
    }
  }, [gettingEntityDetails]);

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName, doNotShowError }) =>
    getCustomerOrEntityLocalAndLenderError({
      fieldError,
      localState: {},
      fieldName,
      localFieldName,
      doNotShowError,
      isEntity: true,
    });

  const renderPpsrComponent = (entitys) => {
    if (entitys?.ppsr) {
      if (entitys?.ppsr?.registrations?.length > 0) {
        return (
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600}>
              PPSR
            </Typography>
            <Grid container item direction="row">
              <Grid item sm={2} xs={12}>
                <Typography variant="subtitle2" fontWeight={600} style={{ margin: "0 0 5px" }}>
                  Type
                </Typography>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Typography variant="subtitle2" fontWeight={600} style={{ margin: "0 0 5px" }}>
                  Security group
                </Typography>
              </Grid>
              <Grid item sm={2} xs={12}>
                <Typography variant="subtitle2" fontWeight={600} style={{ margin: "0 0 5px" }}>
                  Start date
                </Typography>
              </Grid>
              <Grid item sm={2} xs={12}>
                <Typography variant="subtitle2" fontWeight={600} style={{ margin: "0 0 5px" }}>
                  End date
                </Typography>
              </Grid>
            </Grid>
            {entitys?.ppsr?.registrations.map((registration, index) => (
              <Grid key={registration.registrationNumber ?? `${registration.startTime}-${index}`} container item direction="row">
                <Grid item sm={2} xs={12}>
                  <Typography variant="body2" style={{ margin: "0 0 5px" }}>
                    {registration.collateralClassDescription}
                  </Typography>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Typography variant="body2" style={{ margin: "0 0 5px" }}>
                    {registration.securityPartyGroups.map((group, groupIndex) => (
                      <Stack key={`${group.organisationName}-${groupIndex}`}>{group.organisationName}</Stack>
                    ))}
                  </Typography>
                </Grid>
                <Grid item sm={2} xs={12}>
                  <Typography variant="body2" style={{ margin: "0 0 5px" }}>
                    {moment(registration.startTime, "DD-MM-YYYY").format("DD-MM-YYYY")}
                  </Typography>
                </Grid>
                <Grid item sm={2} xs={12}>
                  <Typography variant="body2" style={{ margin: "0 0 5px" }}>
                    {registration.endTime}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        );
      } else {
        return (
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} textAlign={"center"} sx={{ padding: "15px" }}>
              No PPSR data available
            </Typography>
          </Grid>
        );
      }
    } else {
      return null;
    }
  };

  const flipAddressBtnText =
    isManualAddress?.show && isManualAddress?.entityId === entitys?._id
      ? "Use Google Address Search"
      : "Add Address Manually";

  const isAsicCardDisabled = !canUsePaidServices || gettingASICData === entitys?._id || isDisableEdit;
  const isEquifaxCardDisabled = !canUsePaidServices || gettingCreditProfile === entitys?._id || isDisableEdit;

  const paidServicesTooltip = canUsePaidServices ? null : <PaidServicesTooltip />;

  // Each provider's checks share one loader, so the whole group is disabled while any of them runs.
  const toServiceCards = (checks, { isGroupLoading, isDisabled, onClick }) =>
    checks
      .filter(({ isAvailable }) => isAvailable)
      .map(({ check, logos }) => ({
        id: check,
        logos,
        ...ENTITY_SERVICE_CHECK_COPY[check],
        isLoading: isGroupLoading && activeServiceCheck === check,
        isDisabled,
        tooltip: paidServicesTooltip,
        onClick: () => onClick(check),
      }));

  // Offered for an active company until the extract has brought the directors in.
  const canGetAsicExtract =
    entitys?.status === entityStatus.active && Boolean(entitys?.acn) && !entitys?.directors?.[0];
  const asicCards = toServiceCards(
    [
      {
        check: ENTITY_SERVICE_CHECKS.ASIC_EXTRACT,
        logos: [SERVICE_PROVIDER_LOGOS.asic],
        isAvailable: canGetAsicExtract && isAsicExtractEnabled && servicePermission?.asic_extract,
      },
      {
        check: ENTITY_SERVICE_CHECKS.ASIC_EXTRACT_PPSR,
        logos: [SERVICE_PROVIDER_LOGOS.asic, SERVICE_PROVIDER_LOGOS.ppsr],
        isAvailable: canGetAsicExtract && isAsicExtractPPSREnabled && servicePermission?.asic_extract_ppsr,
      },
    ],
    {
      isGroupLoading: gettingASICData === entitys?._id,
      isDisabled: isAsicCardDisabled,
      onClick: handle.getAsicExtract,
    },
  );

  // Offered for a company (not a sole trader) until a report has been retrieved.
  const canGetEquifaxReport =
    Boolean(entitys?.acn) && entitys?.entityType !== entityType.soleTrader && !entitys?.creditProfile?.publicReports;
  const equifaxCards = toServiceCards(
    [
      {
        check: ENTITY_SERVICE_CHECKS.EQUIFAX_REPORT,
        logos: [SERVICE_PROVIDER_LOGOS.equifax],
        isAvailable:
          canGetEquifaxReport && isEquifaxCommercialReportEnabled && servicePermission?.equifax_commercial_report,
      },
      {
        check: ENTITY_SERVICE_CHECKS.EQUIFAX_SCORED_REPORT,
        logos: [SERVICE_PROVIDER_LOGOS.equifax],
        isAvailable:
          canGetEquifaxReport &&
          isEquifaxScoredCommercialReportEnabled &&
          servicePermission?.equifax_scored_commercial_report,
      },
    ],
    {
      isGroupLoading: gettingCreditProfile === entitys?._id,
      isDisabled: isEquifaxCardDisabled,
      // A scored report affects the entity's score, so it is confirmed first.
      onClick: (check) =>
        check === ENTITY_SERVICE_CHECKS.EQUIFAX_SCORED_REPORT
          ? setScoredEquifaxModalOpen(true)
          : handle.getEquifaxReport(check),
    },
  );

  const isAddressAccessible =
    (entitys?.addresses.length === 0 || (entitys?.addresses.length === 1 && entitys?.directors?.length <= 0)) &&
    entitys?.entityType;

  return (
    <Grid
      container
      style={{
        margin: "0 0 30px",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
      }}
    >
      <Grid container item xs={12} sm={12} md={2}>
        <BusinessTwoToneIcon style={{ marginRight: "10px" }} />
        <Typography fontWeight={600}>{`Entity #${index + 1}`}</Typography>
      </Grid>

      <Grid
        container
        item
        xs={12}
        sm={12}
        md={10}
        style={{
          padding: "0 0 0 10px",
        }}
      >
        {entitys && (
          <Grid
            item
            sm={12}
            md={12}
            key={entitys?.entityName}
            id={`entity-${entitys?._id}`}
            style={{ margin: "0 0 20px 0" }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" style={{ margin: "0 0 20px" }}>
              <Typography>{entitys?.entityName}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={continuity}
                    onChange={handle.onChangeFieldContinuity}
                    color="primary"
                    disabled={isDisableEdit}
                    variant={continuity ? "solid" : "outlined"}
                  />
                }
                label="Continuity"
                labelPlacement="start"
                sx={{
                  justifyContent: "flex-start",
                  width: "60%",
                  gap: 0.5,
                  mr: 1,
                }}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={isDisableEdit}
                onClick={() => {
                  setFieldsErrors(false);
                  dispatch(
                    removeEntity({
                      applicationId,
                      entityId: entitys._id,
                    }),
                  );
                }}
              >
                Remove entity
              </Button>
            </Stack>
            <Grid item container style={{ margin: " 0 0 10px" }}>
              {entitys?.abn && (
                <Grid item lg={4} md={4} sm={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    ABN
                  </Typography>
                  <Typography variant="body2">{readableABNString(entitys?.abn)}</Typography>
                </Grid>
              )}

              {entitys?.acn && (
                <Grid item lg={4} md={4} sm={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    ACN
                  </Typography>
                  <Typography variant="body2">{readableACNString(entitys?.acn)}</Typography>
                </Grid>
              )}

              {entitys?.gst && (
                <Grid item lg={4} md={4} sm={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    GST
                  </Typography>
                  <Typography variant="body2">
                    {entitys?.gst ? `${moment().diff(moment(entitys?.gst), "months")} months` : "Not registered"}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Grid container style={{ margin: "0 0 10px 0" }}>
              {entitys?.entityName && (
                <Grid item lg={4} md={4} sm={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Entity name
                  </Typography>
                  <Typography variant="body2">{entitys?.entityName}</Typography>
                </Grid>
              )}

              {entitys?.entityType && (
                <Grid item lg={4} md={4} sm={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Entity type
                  </Typography>
                  <Typography variant="body2">{entitys?.entityType}</Typography>
                </Grid>
              )}

              {entitys?.timeInBusiness && (
                <Grid item lg={4} md={4} sm={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Time in business
                  </Typography>
                  <Typography variant="body2">
                    {`${moment().diff(moment(entitys?.timeInBusiness), "months")} months`}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid container style={{ margin: "0 0 10px 0" }}>
              {entitys?.status && (
                <Grid item lg={4} md={4} sm={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Status
                  </Typography>
                  <Typography variant="body2">{entitys?.status}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                {entitys?.businessNames?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Business names
                    </Typography>
                    <Typography variant="body2">
                      {entitys?.businessNames.map((name, i) => (
                        <span key={`${name}-${i}`}>
                          {entitys?.businessNames.length - 1 === i ? `${name}` : `${name}, `}
                        </span>
                      ))}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
            {entitys?.creditProfile && entitys?.creditProfile?.publicReports && (
              <Grid container style={{ margin: "0 0 10px 0" }}>
                {entitys?.addresses?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Other Information
                    </Typography>
                    <Typography variant="body2">
                      External Administrations: {entitys?.creditProfile.publicReports?.externalAdministrations}
                      <br />
                      Previous Company Names: {entitys?.creditProfile.publicReports?.previousCompanyNames}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}

            <Grid container style={{ margin: "0 0 10px 0" }}>
              {entitys?.addresses?.length > 1 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Business addresses
                  </Typography>
                  {entitys?.addresses.map((address, index) => (
                    <Typography key={address._id ?? `${address.fullAddress}-${index}`} variant="body2">
                      {address.fullAddress}
                    </Typography>
                  ))}
                </Grid>
              )}
            </Grid>
            <Grid container style={{ margin: "0 0 20px 0" }}>
              {renderPpsrComponent(entitys)}
            </Grid>
            {/* Credit Score Section */}
            {entitys?.creditProfile && entitys?.creditProfile?.score && (
              <Grid sx={{ marginBottom: "20px" }}>
                <Typography
                  fontWeight={600}
                  style={{
                    marginBottom: "10px",
                  }}
                >
                  Company Credit Report
                </Typography>
                <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem />}>
                  <Grid item sm={7} md={6} xs={12} style={{ marginBottom: "20px" }}>
                    <Grid container item md={12} sm={12} sx={{ marginBottom: "20px" }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Equifax Bureau Score
                      </Typography>
                      <CreditScoreGradientGraph score={entitys?.creditProfile?.score} />
                    </Grid>
                  </Grid>
                  <Grid item sm={5} md={6} xs={12}>
                    <Grid item xs={12} style={{ marginBottom: "15px" }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Score key contributing factors
                      </Typography>
                    </Grid>
                    <Grid container item spacing={1}>
                      {entitys?.creditProfile?.scoreData?.[0] &&
                        entitys?.creditProfile?.scoreData?.[0]?.["cenq:contributing-factor-list"]?.[
                          "cenq:contributing-factor"
                        ]?.map((factor) => (
                          <Grid key={factor["cenq:score-impactor"]._text} container item md={12} sm={12}>
                            <Grid item sm={2} direction="row" style={{ paddingTop: "4px" }}>
                              <Typography variant="subtitle2">
                                {setTicksAndCrosses(factor["cenq:score-impactor"]?.["_text"])}
                              </Typography>
                            </Grid>
                            <Grid item sm={10}>
                              <Stack>
                                <Typography variant="subtitle2">{factor["cenq:score-factor"]?.["_text"]}</Typography>
                                <Typography variant="body2">
                                  {factor["cenq:contributing-factor-description"]?.["_text"]}
                                </Typography>
                              </Stack>
                            </Grid>
                          </Grid>
                        ))}
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            )}
            {entitys?.creditProfile && (
              <Grid container style={{ marginBottom: "60px" }}>
                {/* Credit History Section */}
                {entitys?.creditProfile && entitys?.creditProfile?.creditHistory && (
                  <Grid item xs={12} sm={12} md={12}>
                    <Typography
                      fontWeight={600}
                      style={{
                        marginBottom: "10px",
                      }}
                    >
                      Credit History Snapshot
                    </Typography>
                    <Grid container item spacing={{ xs: 1, md: 2, sm: 2 }} direction="row" sm={12} xs={12} md={12}>
                      <Grid item sm={12} xs={6} md={6}>
                        <Grid container item marginBottom={1}>
                          <Grid item sm={6} xs={7} md={6}></Grid>
                          <Grid
                            item
                            sm={3}
                            xs={2}
                            md={2}
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              textAlign: "end",
                            }}
                          >
                            Number
                          </Grid>
                          <Grid
                            item
                            sm={3}
                            xs={3}
                            md={4}
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              textAlign: "end",
                            }}
                          >
                            Amount
                          </Grid>
                        </Grid>
                        {creditHistoryValue?.map((item, index) => (
                          <Grid key={`${item.name}-${index}`} container item spacing={1}>
                            <Grid
                              item
                              sm={6}
                              xs={7}
                              md={6}
                              style={{
                                fontSize: "14px",
                                margin: "0 0 2px",
                                fontWeight: [0, 3, 6].includes(index) ? 600 : 400,
                              }}
                            >
                              {`${transformCreditHistoryName(item.name)}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={3}
                              md={2}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {`${item.count}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={3}
                              md={4}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {formatAmount(item?.amount)}
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>

                      {/* Defaults List */}
                      <Grid item sm={12} xs={6} md={6}>
                        <Grid container item marginBottom={1}>
                          <Grid item sm={6} xs={7} md={6}></Grid>
                          <Grid
                            item
                            sm={3}
                            xs={2}
                            md={3}
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              textAlign: "end",
                            }}
                          >
                            Number
                          </Grid>
                          <Grid
                            item
                            sm={3}
                            xs={3}
                            md={3}
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              textAlign: "end",
                            }}
                          >
                            Amount
                          </Grid>
                        </Grid>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Defaults
                        </Typography>
                        {creditHistoryDefaults?.paymentDefaultsList?.map((item, index) => (
                          <Grid key={`${item.name}-${index}`} container item>
                            <Grid
                              item
                              sm={6}
                              xs={7}
                              md={6}
                              style={{
                                fontSize: "14px",
                                margin: "0 0 2px",
                              }}
                            >
                              {`${transformCreditHistoryName(item.name)}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={2}
                              md={3}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {`${item.count}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={3}
                              md={3}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {formatAmount(item?.amount)}
                            </Grid>
                          </Grid>
                        ))}
                        <Typography variant="subtitle2" fontWeight={600}>
                          Category of Default
                        </Typography>
                        {creditHistoryDefaults?.categoryDefaultsList?.map((item, index) => (
                          <Grid key={`${item.name}-${index}`} container item>
                            <Grid
                              item
                              sm={6}
                              xs={7}
                              md={6}
                              style={{
                                fontSize: "14px",
                                margin: "0 0 2px",
                              }}
                            >
                              {`${transformCreditHistoryName(item.name)}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={2}
                              md={3}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {`${item.count}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={3}
                              md={3}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {formatAmount(item?.amount)}
                            </Grid>
                          </Grid>
                        ))}
                        <Typography variant="subtitle2" fontWeight={600}>
                          Default Status
                        </Typography>
                        {creditHistoryDefaults?.statusDefaultsList?.map((item, index) => (
                          <Grid key={`${item.name}-${index}`} container item>
                            <Grid
                              item
                              sm={6}
                              xs={7}
                              md={6}
                              style={{
                                fontSize: "14px",
                                margin: "0 0 2px",
                              }}
                            >
                              {`${transformCreditHistoryName(item.name)}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={2}
                              md={3}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {`${item.count}`}
                            </Grid>
                            <Grid
                              item
                              sm={3}
                              xs={3}
                              md={3}
                              style={{
                                fontSize: "14px",
                                textAlign: "end",
                              }}
                            >
                              {formatAmount(item?.amount)}
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}

            {isAddressAccessible && (
              <Grid container item sm={12} md={12} xl={12} mt="-20px" mb="30px" alignItems="center">
                {isManualAddress?.entityId !== entitys?._id && (
                  <Grid item sm={8}>
                    {/* <Typography variant="caption">Enter address</Typography> */}
                    <GoogleAddressSearch
                      customerId={entitys?._id}
                      singleCustomerId={entities?.[0]?._id}
                      applicationId={applicationId}
                      guarantorFieldErrors={fieldsErrors}
                      setGuarantorFieldErrors={setFieldsErrors}
                      handleCreateAddress={handle.addAddress}
                      handleUpdateAddress={handle.updateAddress}
                      handleRemoveAddress={handle.removeAddress}
                      isEntity={true}
                      disabled={isDisableEdit}
                      address={entitys ? entitys?.addresses?.slice(-1)[0] : ""}
                      isEmptyAddress={entitys && !entitys?.addresses?.slice(-1)[0] ? true : false}
                      addressFieldName={`entityAddress_${entitys?._id}_0`}
                      validateComponentField={validateField}
                      requiredFieldName={`entityAddress_${entitys?._id}`}
                    />
                  </Grid>
                )}

                {isManualAddress?.show && isManualAddress?.entityId === entitys?._id && (
                  <Grid item sm={12} md={12}>
                    <ManualAddress entity={entitys} applicationId={application?._id} isDisable={isDisableEdit} />
                  </Grid>
                )}

                <Grid
                  item
                  sm={12}
                  md={3}
                  xl={3}
                  style={{ margin: isManualAddress?.entityId === entitys?._id ? "10px 0 0 0" : "0 0 0 10px" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={gettingAddressDetails || isDisableEdit} // only single manual address should be open at time
                    onClick={() => {
                      const { show, entityId } = isManualAddress;
                      if (show && entityId === entitys?._id) {
                        dispatch(saveValue({ isManualAddress: { show: false, entityId: null } }));
                      } else {
                        handle.addManualAddress(entitys?._id);
                      }
                    }}
                    style={{
                      height: "36px",
                    }}
                  >
                    {gettingAddressDetails ? "Adding Address..." : flipAddressBtnText}
                  </Button>
                </Grid>
              </Grid>
            )}

            <Grid container item style={{ margin: "-10px 0 30px 0" }}>
              <Grid container item spacing={1}>
                {!isDemoCommercialApplication && (
                  <Grid item md={4} sm={5}>
                    <TextField
                      fullWidth
                      id="outlined-basic"
                      type="text"
                      name={`turnover_${entitys?._id}`}
                      label="Turnover (yearly)"
                      variant="filled"
                      size="small"
                      disabled={isDisableEdit}
                      value={turnover?.[entitys?._id] || entitys?.turnover || ""}
                      error={getLocalAndLenderError({
                        fieldName: `turnover_${entitys._id}`,
                        doNotShowError: !!(turnover?.[entitys?._id] || entitys?.turnover),
                      })}
                      helperText={getLocalAndLenderError({
                        fieldName: `turnover_${entitys._id}`,
                        doNotShowError: !!(turnover?.[entitys?._id] || entitys?.turnover),
                      })}
                      onChange={(event) =>
                        handle.onChangeFieldTurnover(event?.target?.value, `turnover_${entitys?._id}`)
                      }
                      onBlur={(event) => {
                        if (entitys?.turnover !== event.target.value) {
                          handle.entityFn({
                            entityId: entitys?._id,
                            turnover: event.target.value,
                          });
                        }
                      }}
                    />
                  </Grid>
                )}
                <Grid item md={8} sm={7}>
                  <Autocomplete
                    value={industryTypeValue}
                    options={sortedIndustryList}
                    disabled={isDisableEdit}
                    name={`industryType_${entitys._id}`}
                    onChange={(event, newValue) => {
                      handle.onChangeFieldIndustryType(newValue?.value, `industryType_${entitys._id}`);
                    }}
                    onBlur={(event) => {
                      if (entitys?.industryType !== event.target.value) {
                        handle.entityFn({
                          entityId: entitys?._id,
                          industryType: event?.target?.value,
                          ...(!isProfessionalServicesShown && {
                            professionalServices: "",
                          }),
                        });
                        if (!isProfessionalServicesShown) {
                          setProfessionalServices("");
                        }
                      }
                    }}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        {...params}
                        name={`industryType_${entitys._id}`}
                        label="Industry or business type"
                        variant="filled"
                        error={getLocalAndLenderError({
                          fieldName: `industryType_${entitys._id}`,
                          doNotShowError: !!industryTypeValue,
                        })}
                        helperText={getLocalAndLenderError({
                          fieldName: `industryType_${entitys._id}`,
                          doNotShowError: !!industryTypeValue,
                        })}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            {isProfessionalServicesShown && (
              <Grid container item style={{ margin: "-10px 0 30px 0" }}>
                <Grid item sm={8}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="flex-start">
                    <Autocomplete
                      value={professionalServices}
                      options={professionalServicesList}
                      disabled={isDisableEdit}
                      name={`professionalServices_${entitys._id}`}
                      onChange={(_event, value) => {
                        setProfessionalServices(value ?? "");
                        handle.onChangeFieldProfessionalServices(`professionalServices_${entitys._id}`, value ?? "");
                      }}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          name={`professionalServices_${entitys._id}`}
                          label="Professional Services"
                          variant="filled"
                          error={getLocalAndLenderError({
                            fieldName: `professionalServices_${entitys._id}`,
                            doNotShowError: !!professionalServices,
                          })}
                          helperText={getLocalAndLenderError({
                            fieldName: `professionalServices_${entitys._id}`,
                            doNotShowError: !!professionalServices,
                          })}
                        />
                      )}
                      sx={{ flex: "2 1 auto" }}
                    />
                    <Tooltip title="This field is used to determine suitable professional products">
                      <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                        <InfoIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
            )}
            {entitys?.status && !validEntityStatus?.includes(entitys?.status) && (
              <Grid container style={{ marginBottom: "5px", paddingBottom: "10px" }}>
                <Typography fontSize="13px" fontWeight={600}>
                  Note :{" "}
                </Typography>
                <Typography color="#d32f2f" fontSize="13px" fontWeight={600}>
                  This is a Deregistered ASIC organisation. Current ASIC Extracts cannot be purchased for Deregistered.
                </Typography>
              </Grid>
            )}
            {gettingEquifaxCreditCompanyFile && (
              <Typography variant="caption">{FILE_AWAITING_MESSAGE.gettingEquifaxCreditPDFMsg}</Typography>
            )}
            {!isDemoCommercialApplication && (
              <Grid container style={{ margin: "10px 0 5px 0" }}>
                {entitys?.documents
                  ?.filter((doc) => doc.name !== "Financial statement")
                  ?.reduce((acc, current) => {
                    const x = acc.find((item) => item._id === current._id);
                    if (!x) {
                      acc.push(current);
                    }
                    return acc;
                  }, [])
                  ?.map((doc, index) => (
                    <Grid key={doc?._id || index} style={{ alignItems: "start", marginRight: "10px" }}>
                      <Document document={doc} name="asicDocument" />
                    </Grid>
                  ))}
                {/* Make loader for equifax pdf load condition : credit profile data should be there and file is missing */}
              </Grid>
            )}
            {!hideAdvancedActions && (
              <Grid container spacing={1}>
                <EntityOtherDocuments entity={entitys} applicationId={application?._id} isDisableEdit={isDisableEdit} />
              </Grid>
            )}

            {!hideAdvancedActions && (
              <>
                <EntityServiceCardGroup cards={asicCards} />
                <EntityServiceCardGroup cards={equifaxCards} />
                <EntityCommercialCreditChecks
                  entity={entitys}
                  applicationId={applicationId}
                  isDisableEdit={isDisableEdit}
                />
                <ConfirmationDialog
                  isOpen={scoredEquifaxModalOpen}
                  setIsOpen={setScoredEquifaxModalOpen}
                  handle={() => handle.getEquifaxReport(ENTITY_SERVICE_CHECKS.EQUIFAX_SCORED_REPORT)}
                  title="Confirmation"
                  message={
                    // Spans: the dialog already wraps its message in a paragraph.
                    <Typography component="span">
                      <Typography component="span" sx={{ display: "block", fontWeight: "bold" }}>
                        A scored Company Credit Report will impact the score of the entity.
                      </Typography>{" "}
                      Please ensure your client is aware and consents.
                    </Typography>
                  }
                />
              </>
            )}

            {isDemoCommercialApplication && entitys?.acn === "003608795" && entitys?.directors?.[0] && (
              <>
                {entitys?.documents?.filter((doc) => doc.name === "Financial statement").length > 0 && (
                  <Grid container spacing={1} justifyContent="center">
                    <EntityFinancialStatement applicationId={application?._id} entityId={entitys?._id} />
                  </Grid>
                )}

                <Grid container spacing={1} justifyContent="center" mt={5}>
                  <EntityFinancialCommitments applicationId={application?._id} entityId={entitys?._id} />
                </Grid>

                <Grid container style={{ margin: "20px 0 5px 0" }}>
                  {entitys?.documents?.map((doc, index) => (
                    <Grid key={doc?._id || index} style={{ alignItems: "start", marginRight: "10px" }}>
                      <Document document={doc} name={doc?.name} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Grid>
        )}

        {gettingEntityDetails && (
          <Grid style={{ margin: "0 0 40px" }}>
            <Grid>
              <Typography style={{ margin: "0 0 10px" }}>Getting entity details...</Typography>
            </Grid>
            <Grid>
              {handle.loadingGridFirst}
              {handle.loadingGridFirst}
              {handle.loadingGridSecond}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
});

export default Entity;
